import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import type {
  ChannelType,
  Message,
  MessageAttachment,
  WhatsAppWindow,
} from "../types";
import { clearDraft, loadDraft, saveDraft } from "../lib/drafts";
import { queryKeys } from "../lib/queryKeys";
import {
  forwardBody,
  forwardSubject,
  quotedReply,
  replyAllCc,
  replySubject,
  type ComposerIntent,
} from "../lib/quoting";
import { RecipientInput, type Recipient } from "./RecipientInput";
import { MissingRecipients } from "./MissingRecipients";
import type { Participant } from "../lib/participants";
import { ResizeHandle } from "./ui/ResizeHandle";
import { usePaneSize } from "../lib/pane-size";
import { ComposerTrailer } from "./ComposerTrailer";
import { cn } from "./ui/cn";
import {
  MessageEditor,
  editorText,
  type PendingUpload,
} from "./editor/MessageEditor";

type Mode = "reply" | "note";

/**
 * The writing surface, in pixels, and what it may be dragged to.
 *
 * The default is taller than the old `min-h-32` it replaces, and it can afford
 * to be: the thread above gave up the best part of eighty pixels of chrome in
 * the same change -- the filters moved up into the view's own header row, and
 * the ticket header stopped spending a line of its own on "Last activity". The
 * reply box got that space rather than the conversation, because a reply is
 * what an agent came to this screen to write.
 *
 * The ceiling is what stops a drag carrying the send button off the bottom of
 * the window; the floor is roughly three lines, below which the box is no
 * longer somewhere you would write a sentence.
 */
const SURFACE_DEFAULT = 208;
const SURFACE_MIN = 96;
const SURFACE_MAX = 640;

/**
 * Reply / internal-note composer.
 *
 * Mode is a deliberate, visible toggle rather than a subtle tab: sending an
 * internal note to a customer by accident is the single worst mistake this
 * screen can produce, so the note mode is coloured differently and the send
 * button changes wording.
 *
 * The writing surface itself is MessageEditor, shared with the new-ticket
 * dialog so both get formatting, canned responses, emoji and attachments.
 *
 * The signature is shown but not edited -- see ComposerTrailer. It used to be
 * seeded into the editor, which meant the writing area was whatever strip was
 * left above it and a click aimed at the end of a sentence landed inside the
 * signature instead. It is now rendered below the surface, read-only, and the
 * send path appends it.
 */
export function Composer({
  ticketId,
  ticketChannelId,
  replyChannelId,
  ticketChannel,
  hasEmailThread,
  ticketSubject,
  ticketContact,
  ticketContactEmail,
  people,
  replyTo,
  intent,
  onIntentHandled,
  onDraftChange,
  onSent,
}: {
  ticketId: number;
  /** The alias the ticket arrived at; the default to reply from. */
  ticketChannelId: number;
  /**
   * The email channel that answers for this one, when the ticket did not arrive
   * on an address at all -- a web form. Null on every channel that can answer
   * for itself, where `ticketChannelId` is the address.
   */
  replyChannelId: number | null;
  /**
   * The channel the ticket lives on.
   *
   * Drives what this composer offers rather than what it looks like. Cc, a
   * From alias, forwarding and a signature are all email ideas with no WhatsApp
   * equivalent, and showing a control that the server will refuse is worse than
   * not showing it -- the agent finds out after writing the message.
   */
  ticketChannel: ChannelType;
  /**
   * The conversation already exists in Gmail, which fixes which address answers
   * it.
   *
   * A Gmail thread id belongs to one mailbox, so a reply sent from an alias is
   * refused by the send path and by Gmail beneath it -- and it used to be
   * refused *silently*, leaving the reply on "Sending…" for ever. The From menu
   * is therefore not offered at all here rather than offered and rejected: the
   * same reasoning as the WhatsApp controls above, which is that a control the
   * server will refuse is worse than no control, because the agent only finds
   * out once the message is written.
   */
  hasEmailThread: boolean;
  /** What a forward's "Fwd: …" subject is built from. */
  ticketSubject: string;
  /**
   * Who the ticket is with, for the Drafts view to name this row by.
   *
   * Carried into the saved draft rather than looked up when that view renders,
   * so the list is answerable from this browser alone -- see lib/drafts.ts.
   */
  ticketContact?: string | null;
  /**
   * The contact's address, which is what the send path falls back to when the
   * thread has no inbound message to answer.
   *
   * Separate from `ticketContact` above, which is a name when there is one and
   * so cannot be shown as an address without occasionally being a lie.
   */
  ticketContactEmail?: string | null;
  /**
   * Everybody on this thread, our own aliases already removed.
   *
   * Passed in rather than gathered here: the ticket screen already asks this
   * question to draw its participants line, and two collections over the same
   * messages would eventually disagree about who is on the thread -- in a panel
   * whose entire purpose is to be trusted about exactly that.
   */
  people?: Participant[];
  /**
   * The message an ordinary reply answers -- newest inbound, or null on a
   * ticket nobody has written to yet. Read for its recipients, to open the
   * composer as a reply all.
   */
  replyTo: Message | null;
  /** Reply / reply all / forward, raised by a message's ⋯ menu. */
  intent: ComposerIntent | null;
  onIntentHandled: () => void;
  onDraftChange: (hasDraft: boolean) => void;
  onSent: () => void;
}) {
  /*
   * Read once, on the way in. The editor is uncontrolled and seeded from its
   * initial value, so restoring in an effect would paint an empty composer and
   * then swap it -- which looks exactly like the draft being lost, for anyone
   * watching the moment they came back to the ticket.
   */
  const [restored] = useState(() => {
    const draft = loadDraft(ticketId);
    if (!draft?.html) return draft;
    /* Written while the signature still lived in the editor. Lifting it out
       here means a note restored tomorrow is not still carrying one, and a
       reply is signed once by the send path rather than twice. */
    return { ...draft, html: withoutSignatureBlock(draft.html) };
  });

  const [mode, setMode] = useState<Mode>(restored?.mode ?? "reply");
  const [html, setHtml] = useState(restored?.html ?? "");
  const [thenStatus, setThenStatus] = useState<"open" | "closed">("closed");
  const [uploads, setUploads] = useState<PendingUpload[]>(
    restored?.uploads ?? [],
  );

  /*
   * Files on the message being forwarded, to go out again with it.
   *
   * Ids and names only. The bytes are not uploaded a second time -- an inbound
   * Gmail attachment may never have been fetched at all, and asking the browser
   * to download and re-post a 20MB remittance to send it three feet is work
   * nobody needs done. The server resolves the ids, mirrors anything still
   * sitting in Gmail, and attaches them; see `ReplyService.#withCopiedFiles`.
   *
   * Removable, and separately from the originals: forwarding a thread of six
   * mails to a supplier who needs one of the invoices is the ordinary case, and
   * a list you cannot prune would send all six.
   */
  const [carried, setCarried] = useState<MessageAttachment[]>(
    restored?.carried ?? [],
  );
  const [error, setError] = useState<string | null>(null);

  /*
   * How tall this agent likes their reply box, kept across tickets and
   * reloads. Not per ticket: the question is "how much room do I want to
   * write in", which nobody wants to answer again on the next conversation.
   */
  const surface = usePaneSize("pulse.pane.composer", SURFACE_DEFAULT, {
    min: SURFACE_MIN,
    max: SURFACE_MAX,
  });
  const [cc, setCc] = useState<Recipient[]>(restored?.cc ?? []);
  const [showCc, setShowCc] = useState((restored?.cc?.length ?? 0) > 0);
  /*
   * The Cc row folded to one line of names. Reply all on a busy thread fills it
   * with a wall of chips that pushes the message box down the screen, so it
   * arrives folded -- and folded still says who is on it, "Cc Sam, Alex and 4
   * more", which is what the note on seeding below asks of it. One press
   * opens it to edit. Opening Cc by hand opens it unfolded, since that is
   * somebody about to type an address.
   */
  const [ccFolded, setCcFolded] = useState(true);
  /*
   * The To line this reply will go out on, if nobody changes it.
   *
   * Mirrors `resolveRecipients` in the Gmail send service deliberately, and the
   * two have to agree: the customer behind a colleague's forward, else whoever
   * wrote last, falling back to the ticket's contact. `replyTo` is already the
   * newest inbound message, which is the same one the server reads, so this is
   * the same answer rather than a second guess at it.
   */
  const defaultRecipients = useMemo<Recipient[]>(() => {
    if (replyTo?.forwardedFrom) {
      const { name, email } = replyTo.forwardedFrom;
      return [name ? { name, email } : { email }];
    }

    if (replyTo?.fromEmail) {
      return [
        replyTo.fromName
          ? { name: replyTo.fromName, email: replyTo.fromEmail }
          : { email: replyTo.fromEmail },
      ];
    }

    return ticketContactEmail ? [{ email: ticketContactEmail }] : [];
  }, [replyTo, ticketContactEmail]);

  /*
   * The To line as the agent changed it, or null for the default above.
   *
   * For mail a colleague forwarded in, above all. Ingest finds the customer in
   * the forwarded header when the header names an address, but Outlook against
   * Exchange often writes only a name -- and then the default is the colleague,
   * and the agent needs to put the customer there by hand. It is also simply
   * right to be able to: a thread that has moved to somebody else is a thing
   * the agent can see and the server cannot.
   *
   * There is no separate "change" step: the To line is always the chips, as
   * in Gmail, and the first add or remove is what turns the default into an
   * override. Edited where it is shown, so a reply is never sent somewhere
   * other than the line on screen says.
   */
  const [toOverride, setToOverride] = useState<Recipient[] | null>(
    restored?.replyTo ?? null,
  );
  const replyRecipients = toOverride ?? defaultRecipients;

  /*
   * On the thread, on neither line of this reply.
   *
   * The question the desk could not answer by looking: an ordinary reply
   * carries no Cc, so everybody the customer had copied in silently drops off
   * it. Naming them is not a complaint -- replying to one person is often
   * right -- it is the difference between choosing that and not noticing it.
   */
  const missing = useMemo(() => {
    const on = new Set(
      [...replyRecipients, ...cc].map((entry) => entry.email.trim().toLowerCase()),
    );
    return (people ?? []).filter(
      (person) => !on.has(person.address.email.trim().toLowerCase()),
    );
  }, [people, replyRecipients, cc]);

  /*
   * Forwarding, and only forwarding.
   *
   * Null is an ordinary reply, which goes to whoever wrote last and has no To
   * line to argue about. A non-null value means the agent chose Forward, and
   * from then on the composer is addressing somebody who is not part of the
   * conversation -- which is a different enough act that it says so on screen,
   * in a colour, until it is either sent or cancelled.
   *
   * Restored with the draft, and it must be: the quoted "Forwarded message"
   * body comes back regardless, so a forward that lost its recipients would
   * quietly become a reply and send the customer a mail meant for somebody
   * else. See the note on Draft.forwardTo.
   */
  const [forwardTo, setForwardTo] = useState<Recipient[] | null>(
    restored?.forwardTo ?? null,
  );

  /*
   * An overridden Subject header, or null to let the send path derive one.
   *
   * The header is normally "Re: " plus whatever the ticket is called, and that
   * is right almost always -- which is why this starts null rather than
   * prefilled. A subject box permanently open on a support desk is a box
   * people edit by accident, and a reply whose subject no longer matches the
   * thread is how a customer's mail client files the answer away from the
   * question.
   *
   * Overriding it is still worth having: a thread that has wandered onto a
   * second issue, and a forward whose "Fwd:" line means nothing to the person
   * receiving it, are both real and both currently unfixable without renaming
   * the ticket -- which changes what the queue calls it for everybody.
   */
  const [subject, setSubject] = useState<string | null>(
    restored?.subject ?? null,
  );

  /**
   * The last value this component put in the subject field by itself.
   *
   * "Re: " plus the ticket's name is offered rather than imposed, and until
   * somebody edits it the field is showing a derivation, not a decision. That
   * matters because the ticket can be renamed from the header two inches
   * above: without this the reply would go out under the name the agent had
   * just finished correcting, from a field that looks like they chose it.
   *
   * A ref rather than state -- it is compared against, never rendered, and
   * making it state would re-render the composer on every keystroke that
   * happens to match.
   */
  const prefilledSubject = useRef<string | null>(null);

  /** Offer the header that would go out if the field were left closed. */
  const deriveSubject = (): string =>
    forwardTo !== null
      ? forwardSubject(ticketSubject)
      : replySubject(ticketSubject);

  /*
   * Follow a rename, but only while the offer stands. The moment the agent
   * types in the field it is theirs, and a rename must not overwrite it.
   *
   * Keyed on the ticket's name alone. `subject` is deliberately not a
   * dependency: it changes on every keystroke, and re-running this then would
   * make the guard above the only thing standing between an agent and their
   * own text being replaced mid-word.
   */
  useEffect(() => {
    if (subject === null || subject !== prefilledSubject.current) return;
    const next = deriveSubject();
    prefilledSubject.current = next;
    setSubject(next);
  }, [ticketSubject]);

  /*
   * Replies default to the address the customer wrote to. Switching is a
   * deliberate act -- answering from an alias they have never seen looks like
   * it came from a stranger.
   */
  const [fromChannelId, setFromChannelId] = useState(
    replyChannelId ?? ticketChannelId,
  );

  /** Bumped to remount the editor, which is how its content is cleared. */
  const [editorKey, setEditorKey] = useState(0);

  /*
   * What the editor is seeded with on mount. Cleared once the reply is away --
   * the remount that empties the composer would otherwise restore the draft
   * that was just sent.
   */
  const [seed, setSeed] = useState(restored?.html ?? "");

  /*
   * The thread this reply will quote, held beside the body rather than in it.
   *
   * Choosing Reply used to paste the whole conversation into the writing
   * surface: the attribution line, the customer's message, and every earlier
   * round trip nested inside it. An agent then wrote three words at the top of
   * a box mostly full of text they had just finished reading on the same
   * screen -- the thread is drawn above the composer, message by message, and
   * a copy of it inside the box is the one place it says nothing new. Worse,
   * it is editable: a stray keystroke rewrites what the customer said, and the
   * reply goes out looking like their own words were altered.
   *
   * So it goes where the signature already goes -- appended on send, previewed
   * under the surface, and out of the way of the sentence being written. What
   * the customer receives is unchanged; email etiquette still wants the quote,
   * and a mail client showing a reply with no history under it is a worse
   * answer than a composer with history in it.
   */
  const [quotedHtml, setQuotedHtml] = useState<string | null>(
    restored?.quotedHtml ?? null,
  );

  const sendFrom = useQuery({
    queryKey: queryKeys.sendFrom,
    queryFn: api.sendFrom,
    staleTime: 5 * 60_000,
  });

  const isWhatsApp = ticketChannel === "whatsapp";

  /*
   * eBay shares WhatsApp's absences -- no Cc, no From, no subject, no quoted
   * trail, no email signature -- and adds two of its own: plain text capped at
   * 2,000 characters, and at most five files of eBay's four kinds, which go
   * out as links eBay fetches. `isMessage` is the first set; the eBay banner
   * below says the rest.
   */
  const isEbay = ticketChannel === "ebay";
  const isMessage = isWhatsApp || isEbay;

  /*
   * The address a reply on this ticket is *expected* to come from.
   *
   * The channel it arrived on, normally -- answering from an alias the customer
   * has never seen looks like it came from a stranger. A web form has no such
   * address: it is a letterbox, so the reply goes out from whichever email
   * channel speaks for the site, and `replyChannelId` is that one.
   *
   * Which matters to the select below as much as to the send. Its options are
   * email channels only, so a web form ticket without this would give it a value
   * that is not among them -- the browser draws the first option, and the agent
   * reads "help@netxl.com" while the request says something else entirely.
   */
  const homeChannelId = replyChannelId ?? ticketChannelId;

  /*
   * The WhatsApp service window, refetched while the composer is open.
   *
   * A minute is short enough that an agent who starts writing with eight
   * minutes left sees the state change before they finish, and long enough not
   * to be a request per keystroke. Only asked for on a WhatsApp ticket: the
   * endpoint answers for any of them, but a poll on every email ticket is a
   * query a minute per open ticket for an answer that never changes.
   */
  const window_ = useQuery({
    queryKey: queryKeys.whatsappWindow(ticketId),
    queryFn: () => api.whatsappWindow(ticketId),
    enabled: isWhatsApp,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  /*
   * Closed until proven open, and only once the answer is actually in.
   *
   * `undefined` while loading means the send button is briefly disabled rather
   * than briefly wrong. Erring the other way would let an agent press Send into
   * a closed window and get a 409 back with their words still in the box, which
   * is the failure this banner exists to prevent.
   */
  const windowState = openWindowState(window_.data);
  const windowClosed = isWhatsApp && windowState?.open !== true;

  /* Every address the desk can send as, so Reply all does not Cc ourselves --
     which arrives back as new inbound mail and has to be tidied up by hand. */
  const ourAddresses = useMemo(
    () => (sendFrom.data ?? []).map((entry) => entry.address),
    [sendFrom.data],
  );

  /*
   * Open as a reply all.
   *
   * A support thread is rarely one person: the customer copies their colleague,
   * their account manager, the address the order was raised under. Replying to
   * the sender alone drops all of them, and nobody notices until somebody asks
   * why they were left off -- by which point the answer they needed is in a
   * mail they never received. So the default is everyone who was on the message
   * being answered, and dropping somebody is the deliberate act.
   *
   * Which way round this goes is a real trade, and it was the other way for a
   * year. The cost of getting it wrong here is a colleague seeing a reply they
   * did not need; the cost of getting it wrong the other way is a colleague not
   * seeing one they did. Only the second is invisible to the agent.
   *
   * Three things make it safe rather than merely convenient. `replyAllCc` drops
   * every address the desk sends from, so we never Cc ourselves into raising a
   * ticket about our own answer. It drops the sender, who is already the To
   * line. And the Cc row is *opened*, not just filled -- an agent must be able
   * to see who is on a reply before sending it, and this would be a bad change
   * if it were silent. It opens folded to a line of names (`ccFolded`), which
   * still says who, without a wall of chips above the message.
   *
   * Runs once, and only on a composer nobody has touched. A restored draft is
   * somebody's work in progress and its Cc line is what they left it as, right
   * down to being empty. It also waits for `sendFrom`: without our own
   * addresses the filter has nothing to filter, and the desk ends up copied on
   * its own reply.
   */
  const seededCc = useRef(false);

  useEffect(() => {
    if (seededCc.current || restored || sendFrom.data === undefined) return;
    seededCc.current = true;

    if (!replyTo) return;
    const others = replyAllCc(replyTo, ourAddresses);
    if (others.length === 0) return;

    setCc(others);
    setShowCc(true);
  }, [restored, replyTo, ourAddresses, sendFrom.data]);

  /*
   * Act on Reply / Reply all / Forward.
   *
   * This overwrites whatever is in the editor, which is why the menu items
   * read as the deliberate acts they are rather than sitting on the bubble as
   * one-click buttons. `nonce` is what makes choosing the same one twice work:
   * an agent who has typed over the quote and wants it back is asking for
   * exactly the same intent again.
   */
  const handled = useRef<number | null>(null);

  useEffect(() => {
    if (!intent || handled.current === intent.nonce) return;
    handled.current = intent.nonce;

    setMode("reply");
    setError(null);
    /*
     * Any of these three changes what the header should say -- Reply and Reply
     * all derive "Re: ", Forward derives "Fwd: " -- so an override typed for
     * the previous one is dropped. Keeping it is how an agent ends up
     * forwarding a conversation under a "Re: " line, or worse, under the
     * subject of the thing they were writing before they changed their mind.
     */
    setSubject(null);

    /*
     * A forward still seeds the surface, and the asymmetry is the point. A
     * forward *is* the message being sent -- the recipient has never seen it,
     * and the agent routinely edits it, trimming a thread down to the part the
     * new reader needs. A quote is context on an answer somebody else already
     * has, which is exactly what nobody edits and nobody should.
     */
    const body = intent.kind === "forward" ? forwardBody(intent.message) : "";

    setSeed(body);
    setHtml(body);
    setQuotedHtml(intent.kind === "forward" ? null : quotedReply(intent.message));
    setEditorKey((key) => key + 1);

    if (intent.kind === "reply-all") {
      const others = replyAllCc(intent.message, ourAddresses);
      setCc(others);
      setShowCc(others.length > 0);
      setForwardTo(null);
      setCarried([]);
    } else if (intent.kind === "forward") {
      /* Cleared, not carried over. Forwarding is starting a new conversation
         with somebody else; keeping the customer's colleagues on it is how a
         forward becomes an accidental reply-all. */
      setCc([]);
      setShowCc(false);
      setForwardTo([]);
      /* What the forward is actually passing on. Inline images are excluded:
         they are the message's own pictures, already inside the body being
         quoted, and listing them would offer to attach a signature logo. */
      setCarried(intent.message.attachments.filter((file) => !file.isInline));
    } else {
      /* Reply, not Reply all -- and it has to say so by emptying the Cc.
         The composer opens as a reply all now, so leaving the line alone
         would make the two menu items do the same thing on the message an
         agent is most likely to use them on. */
      setCc([]);
      setShowCc(false);
      setForwardTo(null);
      setCarried([]);
    }

    onIntentHandled();
  }, [intent, ourAddresses, onIntentHandled]);

  const hasBody = editorText(html).length > 0;
  useEffect(() => {
    onDraftChange(hasBody);
  }, [hasBody, onDraftChange]);

  /*
   * Persist on every change, debounced.
   *
   * Debounced rather than saved per keystroke because this is a synchronous
   * localStorage write on the typing path. The cleanup writes too, so the save
   * that matters most -- the one on the way out, when the agent navigates to
   * another ticket -- is never the one lost to the pending timer.
   */
  useEffect(() => {
    const store = () => {
      /*
       * A forward is not kept.
       *
       * Drafts answers "what have I not sent to a customer", and a forward is
       * not that -- it is a message on its way to a colleague or a supplier,
       * and the desk reported them cluttering the folder they go to for
       * unfinished customer replies. Anything already stored under this ticket
       * is cleared rather than left: a reply draft that becomes a forward has
       * stopped being the thing the stored copy describes, and leaving it would
       * offer to resume a reply whose body is now addressed to somebody else.
       *
       * The cost is real and deliberate: navigate away mid-forward and it is
       * gone. Cancelling the forward puts the composer back to a reply, which
       * is kept again from that moment.
       */
      if (forwardTo !== null) {
        clearDraft(ticketId);
        return;
      }

      saveDraft(ticketId, {
        html,
        cc,
        uploads,
        carried,
        mode,
        forwardTo,
        replyTo: toOverride,
        quotedHtml,
        subject,
        context: {
          subject: ticketSubject,
          ...(ticketContact ? { contact: ticketContact } : {}),
        },
      });
    };
    const timer = window.setTimeout(store, 400);
    return () => {
      window.clearTimeout(timer);
      store();
    };
  }, [
    ticketId,
    html,
    cc,
    uploads,
    carried,
    mode,
    forwardTo,
    toOverride,
    quotedHtml,
    subject,
    ticketSubject,
    ticketContact,
  ]);

  const attemptSend = () => {
    if (blockedByPlaceholder) {
      setOverridePlaceholders(true);
      return;
    }
    send.mutate();
  };

  const send = useMutation({
    mutationFn: async () => {
      const body = html.trim();
      if (mode === "note") return api.addNote(ticketId, body);

      /*
       * The quote goes on here, below what was written, exactly where the
       * composer's preview says it will. WhatsApp is excluded on the way past
       * -- there is no HTML body and no convention of quoting one, and the
       * conversation is a single continuous thread on the customer's phone.
       */
      const outgoing = quotedHtml && !isMessage ? `${body}${quotedHtml}` : body;

      /*
       * WhatsApp takes the body and its attachments and nothing else. The
       * server refuses the rest outright rather than dropping them silently, so
       * sending them would turn every WhatsApp reply into a 400 -- but the
       * controls that produce them are hidden below anyway, and this is the
       * belt to that.
       */
      if (isEbay) {
        return api.reply(ticketId, {
          bodyHtml: body,
          attachments: uploads.length ? uploads : undefined,
          thenStatus,
        });
      }

      if (isWhatsApp) {
        return api.reply(ticketId, {
          bodyHtml: body,
          attachments: uploads.length ? uploads : undefined,
          thenStatus,
        });
      }

      return api.reply(ticketId, {
        bodyHtml: outgoing,
        attachments: uploads.length ? uploads : undefined,
        copyAttachmentIds: carried.length
          ? carried.map((file) => file.id)
          : undefined,
        cc: cc.length ? cc : undefined,
        /* A forward addresses somebody new and says "Fwd:" so they know they
           are being handed a conversation rather than answered. Everything
           else omits all three and goes to whoever wrote last.

           `isForward` rides along rather than being left for the server to
           deduce from the other two, because neither of them is proof: a
           hand-raised ticket's opening message also carries an explicit `to`,
           and "Fwd:" is a string a customer can type. This is the one place
           that actually knows -- the agent picked Forward from a menu. */
        ...(forwardTo?.length
          ? {
              to: forwardTo,
              subject: forwardSubject(ticketSubject),
              isForward: true,
            }
          : toOverride?.length
            ? /* The agent changed who this reply goes to. Same field a
                 forward uses, without the forward's subject or flag -- it is
                 still a reply, to somebody the agent chose. */
              { to: toOverride }
            : {}),
        /*
         * Last, so it wins over the "Fwd:" line above -- an agent who has
         * opened the subject row on a forward and rewritten it meant that
         * rather than the default the menu produced.
         *
         * Trimmed, and dropped when empty: the server treats a blank subject
         * as "derive one", and sending `""` would otherwise put a ticket on
         * the wire with no name at all.
         */
        ...(subject?.trim() ? { subject: subject.trim() } : {}),
        thenStatus,
        /*
         * Never. The composer no longer puts a signature in the body, so the
         * send path is the only thing that signs a reply -- which is what
         * makes the panel below the editor a preview of what will happen
         * rather than a copy of something already done.
         */
        signatureIncluded: false,
        ...(fromChannelId !== homeChannelId ? { fromChannelId } : {}),
      });
    },
    onSuccess: () => {
      setHtml("");
      setSeed("");
      setQuotedHtml(null);
      setEditorKey((key) => key + 1);
      setUploads([]);
      setCc([]);
      setShowCc(false);
      setForwardTo(null);
      setToOverride(null);
      setSubject(null);
      setError(null);
      setOverridePlaceholders(false);
      clearDraft(ticketId);
      onDraftChange(false);
      onSent();
    },
    onError: (cause: unknown) => {
      setError(cause instanceof Error ? cause.message : "Failed to send");
    },
  });

  /*
   * A canned response can leave "[[INSERT RMA HERE]]" behind, and sending that
   * to a customer is the kind of mistake that gets talked about.
   *
   * The first attempt is refused and names what is unfilled; a second click
   * goes through. Blocking outright would be wrong -- an agent editing over a
   * placeholder can legitimately leave the marker in place, and no rule here
   * gets to be more certain than the person who wrote the sentence.
   */
  const placeholders = findPlaceholders(html);
  const [overridePlaceholders, setOverridePlaceholders] = useState(false);

  // A fresh placeholder after an override re-arms the check.
  useEffect(() => {
    if (placeholders.length === 0) setOverridePlaceholders(false);
  }, [placeholders.length]);

  const blockedByPlaceholder =
    mode === "reply" && placeholders.length > 0 && !overridePlaceholders;

  /* A forward with nobody in the To box has no recipient at all -- the server
     would refuse it, and refusing here says so before the agent writes one. */
  const forwardNeedsRecipient = forwardTo !== null && forwardTo.length === 0;
  /* Same for a reply whose To line was opened and emptied. Sending it would
     not fall back to the default -- the agent took that off on purpose. */
  const replyNeedsRecipient =
    mode === "reply" && forwardTo === null && toOverride !== null && toOverride.length === 0;

  /* A note is internal and goes nowhere near Meta, so the window never gates
     one -- an agent must always be able to write down what they found out. */
  const blockedByWindow = mode === "reply" && windowClosed;

  /*
   * The optional headers not yet open -- Cc and Subject. Drawn at the end of
   * the To line on a reply, where Gmail keeps them, and on a row of their own
   * during a forward, which has no To line here to sit on.
   */
  const headerToggles = (
    <>
      {!showCc ? (
        <button
          type="button"
          onClick={() => {
            setShowCc(true);
            setCcFolded(false);
          }}
          className="text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          Cc
        </button>
      ) : null}
      {subject === null ? (
        <button
          type="button"
          /* Opened with what would have gone out anyway, so the field
             starts as an edit rather than as a blank somebody has to
             fill in correctly from memory. */
          onClick={() => {
            const next = deriveSubject();
            prefilledSubject.current = next;
            setSubject(next);
          }}
          className="text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          Subject
        </button>
      ) : null}
    </>
  );

  /*
   * eBay's cap, counted as the server will count it -- near enough. The server
   * flattens the HTML with its own rules and refuses anything over, so this is
   * the courtesy that lets an agent trim while the words are in front of them.
   */
  const ebayLength = isEbay ? plainTextLength(html) : 0;
  const blockedByLength =
    mode === "reply" && isEbay && ebayLength > EBAY_MAX_MESSAGE_LENGTH;

  /*
   * The files Pulse cannot send on eBay, named, and whether there are too many. The
   * type is the server's reading of the bytes at upload, so this agrees with
   * the refusal the send would otherwise meet.
   */
  const ebayRefusedFiles = isEbay
    ? uploads.filter((file) => !EBAY_MEDIA_TYPES.has(file.mimeType.split(";")[0]!.trim()))
    : [];
  const blockedByFiles =
    mode === "reply" &&
    isEbay &&
    (uploads.length > EBAY_MAX_FILES || ebayRefusedFiles.length > 0);

  const canSend =
    hasBody &&
    !send.isPending &&
    !forwardNeedsRecipient &&
    !replyNeedsRecipient &&
    !blockedByWindow &&
    !blockedByLength &&
    !blockedByFiles;

  return (
    <div
      className={cn(
        "border-t px-5 py-3",
        mode === "note"
          ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30"
          : "border-line bg-surface",
      )}
    >
      <div className="mb-2 flex items-center gap-1">
        {(["reply", "note"] as Mode[]).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setMode(option)}
            className={cn(
              "rounded px-2 py-1 text-xs font-medium transition",
              mode === option
                ? option === "note"
                  ? "bg-amber-500 text-white"
                  : "bg-brand-600 text-white"
                : "text-muted hover:bg-surface-2",
            )}
          >
            {option === "reply" ? "Reply to customer" : "Internal note"}
          </button>
        ))}

        {mode === "note" ? (
          <span className="ml-2 text-[11px] text-amber-700 dark:text-amber-400">
            Only visible to your team — never emailed.
          </span>
        ) : null}
      </div>

      {/*
        The service window, stated rather than left to be discovered.

        WhatsApp allows a free-form message only within 24 hours of the
        customer's last one, and outside that only a pre-approved template gets
        through -- which Pulse cannot send yet. So the composer says where the
        clock stands while there is still time to act on it, and says plainly
        what the options are once there is not.
      */}
      {mode === "reply" && isWhatsApp ? (
        <div
          className={cn(
            "mb-2 flex items-start gap-2 rounded-md border px-2 py-1.5 text-[11px]",
            windowState?.open
              ? "border-emerald-500/50 bg-emerald-50/60 text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-300"
              : "border-amber-500/60 bg-amber-50/60 text-amber-800 dark:bg-amber-950/20 dark:text-amber-300",
          )}
        >
          <span aria-hidden className="mt-px">
            {windowState?.open ? "●" : "○"}
          </span>
          <span className="min-w-0">
            {window_.isPending
              ? "Checking the WhatsApp service window…"
              : (windowState?.label ?? "Could not read the WhatsApp service window")}
            {windowClosed && !window_.isPending ? (
              <span className="block text-muted">
                Ring them, or email them if you have an address. An internal note
                still works.
              </span>
            ) : null}
          </span>
        </div>
      ) : null}

      {/*
        What an eBay reply is and is not, said once where it applies rather
        than discovered as a 400: plain text, eBay's cap, no files, and no
        signature -- eBay filters messages carrying contact details, which is
        what an email signature is made of.
      */}
      {mode === "reply" && isEbay ? (
        <div
          className={cn(
            "mb-2 flex items-start justify-between gap-2 rounded-md border px-2 py-1.5 text-[11px]",
            blockedByLength || blockedByFiles
              ? "border-amber-500/60 bg-amber-50/60 text-amber-800 dark:bg-amber-950/20 dark:text-amber-300"
              : "border-line bg-surface-2 text-muted",
          )}
        >
          <span className="min-w-0">
            Sent as an eBay message: plain text and no signature. Up to{" "}
            {EBAY_MAX_FILES} photos (JPEG, PNG, GIF, WebP).
            Leave out email addresses, phone numbers and links — eBay may block
            a message that carries them.
            {ebayRefusedFiles.length > 0 ? (
              <span className="block">
                Only photos can be sent on eBay, not{" "}
                {ebayRefusedFiles.map((file) => file.filename).join(", ")} — remove
                it to send.
              </span>
            ) : uploads.length > EBAY_MAX_FILES ? (
              <span className="block">
                That is {uploads.length} files; send the rest in a second reply.
              </span>
            ) : null}
          </span>
          <span className="shrink-0 tabular-nums">
            {ebayLength.toLocaleString("en-GB")} /{" "}
            {EBAY_MAX_MESSAGE_LENGTH.toLocaleString("en-GB")}
          </span>
        </div>
      ) : null}

      {mode === "reply" &&
      !isMessage &&
      !hasEmailThread &&
      (sendFrom.data?.length ?? 0) > 1 ? (
        <div className="mb-2 flex items-center gap-2">
          <label htmlFor="composer-from" className="text-xs text-muted">
            From
          </label>
          <select
            id="composer-from"
            value={fromChannelId}
            onChange={(event) => setFromChannelId(Number(event.target.value))}
            className={cn(
              "rounded-md border bg-surface px-2 py-1 text-xs text-content outline-none",
              // Flagged when it is not the address that speaks for this ticket.
              fromChannelId === homeChannelId
                ? "border-line"
                : "border-brand-500 text-brand-700 dark:text-brand-300",
            )}
          >
            {(sendFrom.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.address}
              </option>
            ))}
          </select>
          {fromChannelId !== homeChannelId ? (
            <span className="text-[11px] text-muted">
              {ticketChannel === "webform"
                ? "Not the address that answers for the website"
                : "Not the address this ticket came in on"}
            </span>
          ) : null}
        </div>
      ) : null}

      {/*
        A forward's recipients, and the only time this composer shows a To
        line at all. Coloured and captioned because it is the one state in
        which pressing Send does not mail the customer -- an agent who has
        forgotten they chose Forward, and answers the customer into this box,
        sends the answer to the wrong person entirely.
      */}
      {mode === "reply" && !isMessage && forwardTo !== null ? (
        <div className="mb-2 rounded-md border border-brand-500/60 bg-brand-50/50 p-2 dark:bg-brand-950/20">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-brand-700 dark:text-brand-300">
              Forwarding
            </span>
            <span className="text-[11px] text-muted">
              goes to these people instead of the customer
            </span>
            <button
              type="button"
              onClick={() => setForwardTo(null)}
              className="ml-auto text-[11px] text-muted hover:text-brand-600 dark:hover:text-brand-400"
            >
              Cancel forward
            </button>
          </div>
          <div className="flex items-start gap-2">
            <label
              htmlFor="composer-forward-to"
              className="mt-1.5 shrink-0 text-xs text-muted"
            >
              To
            </label>
            <div className="min-w-0 flex-1">
              <RecipientInput
                id="composer-forward-to"
                autoFocus
                value={forwardTo}
                onChange={setForwardTo}
              />
            </div>
          </div>
        </div>
      ) : null}

      {mode === "reply" && !isMessage ? (
        <>
          {/*
            The To line, as Gmail draws it: the addresses themselves, each with
            its own cross, and a box to type more into. Starts on whoever the
            reply would go to anyway -- the customer behind a colleague's
            forward, else whoever wrote last -- and any of them can go,
            the sender included.

            Not during a forward, which has its own To box above; a second one
            naming the customer here would be naming somebody that send is
            deliberately not going to.
          */}
          {forwardTo === null ? (
            <div className="mb-2">
              <div className="flex items-start gap-2">
                <label
                  htmlFor="composer-to"
                  className="mt-1.5 w-7 shrink-0 text-xs text-muted"
                >
                  To
                </label>
                <div className="min-w-0 flex-1">
                  <RecipientInput
                    id="composer-to"
                    value={replyRecipients}
                    onChange={setToOverride}
                    placeholder="Add a recipient"
                  />
                </div>
                {/* Cc and Subject at the end of the To line, where Gmail
                    keeps them -- optional headers, one click away, taking no
                    row of their own until they are wanted. */}
                <div className="mt-1.5 flex shrink-0 gap-2">
                  {headerToggles}
                </div>
              </div>

              {replyNeedsRecipient ? (
                <p className="ml-9 mt-1 text-[11px] text-sla-breached">
                  Add somebody to send this to.
                </p>
              ) : replyTo?.forwardedFrom && toOverride === null ? (
                /* The thread shows the colleague as the sender, so a To line
                   naming somebody else needs its reason said beside it. */
                <p className="ml-9 mt-1 text-[11px] text-faint">
                  Forwarded in by {replyTo.fromName ?? replyTo.fromEmail}
                </p>
              ) : null}
            </div>
          ) : null}

          <MissingRecipients
            missing={forwardTo === null ? missing : []}
            onAddMissing={() => {
              setCc((current) => [
                ...current,
                ...missing.map((person) => person.address),
              ]);
              setShowCc(true);
              /* Unfolded, so the people just added are seen going on. */
              setCcFolded(false);
            }}
          />

          {showCc ? (
            <div className="mb-2 flex items-start gap-2">
              <label
                htmlFor="composer-cc"
                className="mt-1.5 w-7 shrink-0 text-xs text-muted"
              >
                Cc
              </label>
              <div className="min-w-0 flex-1">
                {ccFolded && cc.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setCcFolded(false)}
                    aria-expanded={false}
                    title="Show everybody on Cc"
                    className="flex w-full min-w-0 items-center gap-1 rounded-md py-1.5 text-left text-sm text-content hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    <span className="truncate">{ccSummary(cc)}</span>
                    <span aria-hidden className="shrink-0 text-xs text-faint">▾</span>
                  </button>
                ) : (
                  <RecipientInput
                    id="composer-cc"
                    autoFocus
                    value={cc}
                    onChange={setCc}
                  />
                )}
              </div>
              {!ccFolded && cc.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setCcFolded(true)}
                  aria-expanded
                  className="mt-1.5 shrink-0 text-xs text-faint hover:text-brand-600 dark:hover:text-brand-400"
                >
                  Hide
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setCc([]);
                  setShowCc(false);
                }}
                className="mt-1.5 shrink-0 text-xs text-faint hover:text-brand-600 dark:hover:text-brand-400"
                aria-label="Remove CC"
              >
                ✕
              </button>
            </div>
          ) : null}

          {/*
            The Subject header, below Cc as it is in every mail client.

            Shown only once asked for, and closing it puts the header back to
            the derived one rather than leaving the last typed value in place
            invisibly -- the same argument the forward banner makes, which is
            that a header nobody can see is a header nobody checks.
          */}
          {subject !== null ? (
            <div className="mb-2 flex items-start gap-2">
              <label
                htmlFor="composer-subject"
                className="mt-1.5 shrink-0 text-xs text-muted"
              >
                Subject
              </label>
              <input
                id="composer-subject"
                autoFocus
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                maxLength={998}
                className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1 text-sm text-content outline-none focus:border-brand-500"
              />
              <button
                type="button"
                onClick={() => setSubject(null)}
                className="mt-1.5 shrink-0 text-xs text-faint hover:text-brand-600 dark:hover:text-brand-400"
                aria-label="Use the default subject"
              >
                ✕
              </button>
            </div>
          ) : null}

          {/* During a forward there is no To line to sit at the end of, so
              the optional headers keep a row of their own. */}
          {forwardTo !== null && (!showCc || subject === null) ? (
            <div className="mb-2 flex items-center gap-3">{headerToggles}</div>
          ) : null}
        </>
      ) : null}

      {/*
        The grip, immediately above the editor's own border rather than on it.
        On the border it fought with the toolbar's rounded top corners for the
        same two pixels, and a handle that is sometimes a button is worse than
        one that is always a gutter.
      */}
      <ResizeHandle
        axis="y"
        invert
        label="Reply box"
        size={surface.size}
        min={SURFACE_MIN}
        max={SURFACE_MAX}
        onChange={surface.setSize}
        onReset={surface.reset}
      />

      {/*
        What is going with the forward, and what is not.
        
        Above the editor rather than beside the upload chips below it: these
        were not chosen here, they came with the message, and an agent needs to
        see them before deciding what to trim rather than after writing the
        covering note.
      */}
      {carried.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 px-3 pb-1 pt-2">
          <span className="text-[11px] text-faint">Forwarding:</span>
          {carried.map((file) => (
            <span
              key={file.id}
              className="inline-flex items-center gap-1 rounded-md border border-line bg-surface-2 px-1.5 py-0.5 text-[11px] text-muted"
            >
              <span className="max-w-48 truncate" title={file.filename}>
                {file.filename}
              </span>
              <button
                type="button"
                onClick={() =>
                  setCarried((files) =>
                    files.filter((entry) => entry.id !== file.id),
                  )
                }
                aria-label={`Do not forward ${file.filename}`}
                title={`Do not forward ${file.filename}`}
                className="text-faint hover:text-brand-600 dark:hover:text-brand-400"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <MessageEditor
        key={editorKey}
        initialHtml={seed}
        onChange={setHtml}
        uploads={uploads}
        onUploadsChange={setUploads}
        onError={setError}
        onSubmit={() => {
          if (canSend) attemptSend();
        }}
        allowAttachments={mode === "reply"}
        allowMentions={mode === "note"}
        /*
         * Room to actually write, without taking the conversation's -- and now
         * however much of it the agent asked for.
         *
         * An exact height rather than the `min-h-24 max-h-64 sm:min-h-32` this
         * replaces, and the classes have to go rather than stay as a floor: a
         * `min-h-*` outranks a shorter dragged height, so the handle would
         * have appeared to jam a third of the way down. The surface scrolls,
         * so a forward seeded with a whole quoted email still cannot push the
         * send button off the screen.
         */
        /*
         * No floor, and a ceiling only on a phone.
         *
         * The default `min-h-*` has to go for the reason above -- it outranks
         * a shorter dragged height. The `max-h` is the opposite problem, and a
         * narrow screen is where it shows: `surface.size` is a measurement
         * taken on whatever screen the drag happened on, kept deliberately
         * across tickets and machines, so on a 390-wide phone the reply box
         * and its chrome came to nearly half the viewport and left the
         * conversation a 165-pixel band to read a whole thread through. The
         * agent had not dragged anything; they inherited a desktop default.
         *
         * A cap rather than a smaller stored value, so nothing is written
         * back: turn the phone sideways, or open the same account on a laptop,
         * and the height they actually chose is still there. Gone from `sm`
         * up, where the drag should be the only thing deciding this.
         */
        surfaceHeight="max-h-[30vh] sm:max-h-none"
        surfaceStyle={{ height: surface.size }}
        placeholder={
          mode !== "reply"
            ? "Add a note for your colleagues…  @ mentions someone"
            : isWhatsApp
              ? "Write your WhatsApp reply…  #shortcut inserts a canned response, ⌘/Ctrl + Enter sends"
              : isEbay
                ? "Write your eBay reply…  #shortcut inserts a canned response, ⌘/Ctrl + Enter sends"
                : "Write your reply…  #shortcut inserts a canned response, ⌘/Ctrl + Enter sends"
        }
        /*
          Notes are excluded, and that is the whole of the fix for signatures
          turning up on them: there is no longer any path by which one reaches
          a note. It was possible before only because the block was seeded into
          the editor as text, so switching Reply -> Internal note carried it
          across.
        */
        footer={
          mode === "reply" && !isMessage ? (
            <ComposerTrailer channelId={fromChannelId} quotedHtml={quotedHtml} />
          ) : null
        }
      />

      {blockedByPlaceholder ? (
        <p className="mt-2 text-xs text-red-600">
          Still to fill in:{" "}
          {placeholders.map((text, index) => (
            <span key={index}>
              {index > 0 ? ", " : ""}
              <span className="font-bold">{text}</span>
            </span>
          ))}
          . Press send again to go ahead anyway.
        </p>
      ) : null}

      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {mode === "reply" ? (
          <label className="flex items-center gap-1.5 text-xs text-muted">
            <input
              type="checkbox"
              checked={thenStatus === "closed"}
              onChange={(event) =>
                setThenStatus(event.target.checked ? "closed" : "open")
              }
              className="accent-brand-600"
            />
            Close ticket on send
          </label>
        ) : null}

        <button
          type="button"
          onClick={attemptSend}
          disabled={!canSend}
          className={cn(
            "ml-auto rounded-md px-3 py-1.5 text-sm font-medium text-white transition disabled:opacity-40",
            mode === "note"
              ? "bg-amber-600 hover:bg-amber-500"
              : blockedByPlaceholder
                ? "bg-red-600 hover:bg-red-700"
                : "bg-brand-600 hover:bg-brand-700",
          )}
        >
          {send.isPending
            ? "Sending…"
            : mode === "note"
              ? "Add note"
              : blockedByWindow
                ? "Window closed"
                : blockedByPlaceholder
                  ? "Send anyway"
                  : forwardTo !== null
                    ? "Forward"
                    : "Send reply"}
        </button>
      </div>
    </div>
  );
}

/**
 * Marks a signature block an older composer seeded into the editor.
 *
 * Mirrors SIGNATURE_MARKER in the API's common/signature.ts. Only ever used to
 * recognise a stale draft, so the cost of the two drifting apart is one
 * unstripped signature on a draft written before this change -- and nothing
 * at all after those have been sent or abandoned.
 */
/**
 * The window's state, or undefined when there isn't one to show.
 *
 * A function rather than a ternary at the call site so the discriminant is
 * narrowed once, in a place that names what the two cases mean: a channel with
 * no window at all, and a window whose state we have not read yet, both render
 * the same and neither is "closed".
 */
function openWindowState(
  answer: WhatsAppWindow | undefined,
): Extract<WhatsAppWindow, { applicable: true }> | undefined {
  if (!answer || !answer.applicable) return undefined;
  return answer;
}

const SIGNATURE_MARKER = "data-signature";

/**
 * A draft body with any seeded signature block taken back out.
 *
 * Drafts written before this change are sitting in agents' browsers right now
 * with a signature inside them, and both modes get it wrong: a reply would go
 * out signed twice, and a note -- which never wanted one -- would keep showing
 * the signature that got there by switching modes.
 *
 * Parsed rather than pattern-matched, for the reason findPlaceholders is: this
 * is a live DOM serialisation, so the attribute quoting is whatever the browser
 * produced and a regex tuned to one engine's output quietly stops matching
 * another's.
 */
function withoutSignatureBlock(html: string): string {
  if (!html.includes(SIGNATURE_MARKER)) return html;

  const host = document.createElement("div");
  host.innerHTML = html;
  for (const block of host.querySelectorAll(`[${SIGNATURE_MARKER}]`)) {
    block.remove();
  }
  return host.innerHTML;
}

/**
 * The unfilled placeholders left in a body, by their visible text.
 *
 * Parsed as HTML rather than matched with a regex: the composer's content is a
 * live DOM serialisation, so attribute order and quoting are whatever the
 * browser produced, and a pattern that matched one engine's output would
 * quietly stop matching another's.
 */
function findPlaceholders(html: string): string[] {
  if (!html.includes("data-placeholder")) return [];

  const host = document.createElement("div");
  host.innerHTML = html;

  return [...host.querySelectorAll("[data-placeholder]")]
    .map((node) => (node.textContent ?? "").trim())
    .filter(Boolean);
}

/**
 * Pull email addresses out of free text.
 *
 * Accepts commas, semicolons, spaces and newlines as separators, and tolerates
 * "Name <addr@example.com>" pasted from a mail client. Anything not
 * recognisably an address is dropped: a malformed recipient bounces the whole
 * message, so omitting one beats failing the send, and the caller errors if
 * nothing at all could be parsed.
 */
export function extractEmails(input: string): string[] {
  const found = input.match(/[^\s<>,;"]+@[^\s<>,;"]+\.[^\s<>,;"]+/g) ?? [];
  const seen = new Set<string>();

  return found.reduce<string[]>((list, raw) => {
    const email = raw.trim().replace(/[.,;]+$/, "").toLowerCase();
    if (email && !seen.has(email)) {
      seen.add(email);
      list.push(email);
    }
    return list;
  }, []);
}

/** eBay's ceiling on one message, mirrored from `@helpdesk/ebay`. */
const EBAY_MAX_MESSAGE_LENGTH = 2000;

/**
 * eBay's limits on files, mirrored from `@helpdesk/ebay`'s media.ts. Photos
 * only: they go through eBay Picture Services, and nothing else has been seen
 * to reach a buyer.
 */
const EBAY_MAX_FILES = 5;
const EBAY_MEDIA_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);

/**
 * About how long the composer's HTML is once flattened to text.
 *
 * Paragraph and line breaks count as the newlines the server will turn them
 * into, and entities as one character, so a reply that reads as within the
 * cap here is within it there to a character or two -- and the server, which
 * refuses rather than truncates, is the one that decides.
 */
function plainTextLength(html: string): number {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&[a-z#0-9]+;/gi, "_")
    .trim().length;
}

/**
 * A folded Cc line: two names and a count, the way Gmail's app says it.
 * Names where the address has one, because the line is read for "is my account
 * manager on this", which is a question about people.
 */
function ccSummary(people: Recipient[]): string {
  const names = people.map((person) => person.name || person.email);
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
}
